import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { AuthService } from '../../../../core/services/auth.service';
import { SwalService } from '../../../../core/services/swal.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import {
  LucideAngularModule,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
} from 'lucide-angular';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as AuthActions from '../../../../store/auth/auth.actions';
import {
  selectAuthLoading,
  selectAuthError,
  selectIsAuthenticated,
} from '../../../../store/auth/auth.selectors';
import { ResetPasswordModalComponent } from '../reset-password-modal/reset-password-modal.component';

@Component({
  selector: 'app-login-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AngularPhoneNumberInput,
    LucideAngularModule,
  ],
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.scss'],
})
export class LoginModalComponent implements OnInit {
  readonly LockIcon = LockIcon;
  readonly EyeIcon = EyeIcon;
  readonly EyeOffIcon = EyeOffIcon;

  private readonly SAVED_PHONE_NUMBER_KEY = 'saved_phone_number';

  loginForm = new FormGroup({
    phoneNumber: new FormControl('', [Validators.required]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
    ]),
  });
  preferredCountries: string[] = ['SN', 'CI', 'GA'];

  showPassword = false;
  phoneNumberData: any = null;
  isLoading$: any;
  error$: any;
  isAuthenticated$: any;

  constructor(
    private modalService: ModalService,
    private authService: AuthService,
    private swalService: SwalService,
    private router: Router,
    private store: Store,
    private analyticsService: AnalyticsService
  ) {
    // Log le statut du service Analytics au démarrage
    this.analyticsService.logStatus();
  }

  ngOnInit(): void {
    // Initialiser les observables
    this.isLoading$ = this.store.select(selectAuthLoading);
    this.error$ = this.store.select(selectAuthError);
    this.isAuthenticated$ = this.store.select(selectIsAuthenticated);

    // Charger le numéro de téléphone sauvegardé
    this.loadSavedPhoneNumber();

    // Écouter les erreurs d'authentification
    this.error$.subscribe((error: string | null) => {
      if (error) {
        this.swalService.error('Erreur de connexion', error);
        // Tracker l'échec de connexion avec Firebase Analytics
        this.analyticsService.logEvent('login_failure', {
          error_message: error,
        });
      }
    });

    // Écouter la réussite de la connexion pour sauvegarder le numéro
    this.isAuthenticated$.subscribe((isAuth: boolean) => {
      if (isAuth) {
        // Sauvegarder le numéro de téléphone après une connexion réussie
        this.savePhoneNumber();
        // Tracker la connexion réussie avec Firebase Analytics
        this.analyticsService.logEvent('login_success', {
          method: 'phone_number',
        });
        // this.closeModal();
        // this.router.navigate(['/passport']);
      }
    });
  }

  onPhoneNumberChange(event: any): void {
    this.phoneNumberData = event;
  }

  onSubmit(): void {
    const formValue = this.loginForm.value;
    const fullPhoneNumber = formValue.phoneNumber || '';
    const password = formValue.password || '';

    // Vérifier que les champs obligatoires sont remplis
    if (!fullPhoneNumber || !password) {
      this.swalService.error('Erreur', 'Veuillez remplir tous les champs');
      this.markFormGroupTouched();
      return;
    }

    // Vérifier la longueur minimale du mot de passe
    if (password.length < 6) {
      this.swalService.error(
        'Erreur',
        'Le mot de passe doit contenir au moins 6 caractères'
      );
      return;
    }

    // Sauvegarder le numéro de téléphone avant la connexion
    this.savePhoneNumber();

    // Extraire le numéro local sans l'indicatif (même logique que verify-phone-number)
    let localPhoneNumber = '';

    // Définir les indicatifs des pays préférés
    const countryCodes = {
      SN: '221', // Sénégal
      CI: '225', // Côte d'Ivoire
      CM: '237', // Cameroun
    };

    // Extraire l'indicatif et le numéro
    let indicative = '';
    let phoneNumber = '';

    // Essayer de trouver l'indicatif dans la chaîne
    for (const [country, code] of Object.entries(countryCodes)) {
      if (fullPhoneNumber.includes(code)) {
        const parts = fullPhoneNumber.split(code);
        if (parts.length > 1) {
          indicative = code;
          phoneNumber = parts[1].replace(/[^0-9]/g, ''); // Garder seulement les chiffres
          break;
        }
      }
    }

    // Si aucun indicatif trouvé, essayer une approche différente
    if (!indicative) {
      // Supprimer tous les caractères non numériques sauf le +
      const cleanNumber = fullPhoneNumber.replace(/[^\d+]/g, '');

      // Essayer de trouver un indicatif de 2-3 chiffres
      const match = cleanNumber.match(/^\+?(\d{2,3})(\d+)$/);
      if (match) {
        indicative = match[1];
        phoneNumber = match[2];
      } else {
        this.swalService.error(
          'Erreur',
          'Format de numéro de téléphone invalide'
        );
        return;
      }
    }

    // Pour la Côte d'Ivoire (225), ajouter le 0 initial si manquant
    // Les numéros ivoiriens ont 10 chiffres et commencent par 0
    if (
      indicative === '225' &&
      phoneNumber.length === 9 &&
      !phoneNumber.startsWith('0')
    ) {
      phoneNumber = '0' + phoneNumber;
    }

    localPhoneNumber = phoneNumber;

    // Préparer les données pour l'API
    const loginData = {
      username: localPhoneNumber, // Utiliser le numéro local sans indicatif
      password: password,
    };

    console.log('Login data:', loginData);

    // Dispatch l'action de connexion
    this.store.dispatch(AuthActions.login(loginData));
  }

  closeModal(): void {
    this.modalService.closeAll();
  }

  onForgotPassword(): void {
    this.modalService.open({
      component: ResetPasswordModalComponent,
      title: 'Réinitialisation mot de passe',
      closable: true,
    });
  }

  onRegister(): void {
    // Logique pour s'inscrire
    console.log("S'inscrire");
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Marquer tous les champs comme touchés pour afficher les erreurs
  markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getters pour les erreurs de validation
  get phoneNumberError(): string {
    const control = this.loginForm.get('phoneNumber');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le numéro de téléphone est requis';
      }
    }
    return '';
  }

  get passwordError(): string {
    const control = this.loginForm.get('password');
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return 'Le mot de passe est requis';
      }
      if (control.errors['minlength']) {
        return 'Le mot de passe doit contenir au moins 6 caractères';
      }
    }
    return '';
  }

  /**
   * Charge le numéro de téléphone sauvegardé depuis localStorage
   */
  private loadSavedPhoneNumber(): void {
    try {
      const savedPhoneNumber = localStorage.getItem(
        this.SAVED_PHONE_NUMBER_KEY
      );
      if (savedPhoneNumber) {
        // Pré-remplir le champ avec le numéro sauvegardé
        // Utiliser setTimeout pour s'assurer que le composant est complètement initialisé
        setTimeout(() => {
          this.loginForm.patchValue({
            phoneNumber: savedPhoneNumber,
          });
        }, 0);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du numéro sauvegardé:', error);
    }
  }

  /**
   * Sauvegarde le numéro de téléphone dans localStorage
   */
  private savePhoneNumber(): void {
    try {
      const phoneNumber = this.loginForm.get('phoneNumber')?.value;
      if (phoneNumber) {
        localStorage.setItem(this.SAVED_PHONE_NUMBER_KEY, phoneNumber);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du numéro:', error);
    }
  }
}
